import {
  closeDialog,
  DataStatus,
  FormSubmitHandler,
  openDeleteDialog,
  openDialog,
} from 'platform/components';
import {Hide, VStack} from 'platform/foundation';

import {useEffect, useReducer, useRef} from 'react';

import {add} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  useDeleteServiceOrderJobMutation,
  useGetServiceCaseOrderJobsQuery,
  usePatchServiceOrderJobMutation,
  usePostServiceOrderJobMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {workshopRoutes} from '@dms/routes';
import {handleApiError, queryParams} from '@dms/shared';
import {JobFormProps, JobType, PostponeDialog} from '@dms/shared-aftersales';

import {
  composePath,
  Nullish,
  suffixTestId,
  TestIdProps,
  useNavigate,
  useQueryState,
} from 'shared/web';

import {Job} from '../../../../../components/Job/Job';
import {JobInput, JobInputForm} from '../../../../../components/JobInput/JobInput';
import {JobSegments} from '../../../../../types/JobSegments';
import {CustomPrice} from './CustomPrice';
import {MoveRequest} from './MoveRequest';

interface OrderJobsProps extends TestIdProps {
  orderId: string;
  orderVariantId?: string;
  serviceCaseId: string;
  isReadOnly?: boolean;
  datagridKey?: string;
  isReportButtonDisabled?: boolean;
}

export function OrderJobs(props: OrderJobsProps) {
  const [jobRefreshKey, reloadJob] = useReducer(add(1), 0);

  const navigate = useNavigate();

  const {data, isFetching, isError} = useGetServiceCaseOrderJobsQuery(
    {
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.orderId,
    },
    {
      selectFromResult: (result) => ({
        ...result,
        data: result.data?.filter((job) => !job?.isUncategorized || job?.hasItems),
      }),
    }
  );

  const [deleteServiceOrderJob] = useDeleteServiceOrderJobMutation();
  const [patchServiceOrderJob] = usePatchServiceOrderJobMutation();
  const [postServiceOrderJob] = usePostServiceOrderJobMutation();

  const [_, setOpendJobId] = useQueryState(queryParams.SERVICE_CASE_OPEN_JOB_ID);
  const prevJobIdsRef = useRef<Set<string> | null>(null);

  const onSubmit: FormSubmitHandler<JobInputForm> = async ({job}) => {
    await postServiceOrderJob({
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.orderId,
      body: {name: job, orderVariantId: props?.orderVariantId ?? undefined},
    })
      .unwrap()
      .then((data) => {
        navigate(
          composePath(workshopRoutes.serviceCaseDetail, {
            params: {id: props.serviceCaseId},
            queryParams: {
              [queryParams.SERVICE_CASE_JOB_SEGMENT]: JobSegments.DETAILS,
              [queryParams.SERVICE_CASE_OPEN_JOB_ID]: data?.id ?? '',
              [queryParams.SERVICE_CASE_ORDER_ID]: props.orderId,
            },
          })
        );
      })
      .catch(handleApiError);
  };

  const handleUpdateJob: JobFormProps['onSubmit'] = async (data, reset) => {
    if (!data?.id) {
      return;
    }

    await patchServiceOrderJob({
      body: data,
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.orderId,
      serviceJobId: data?.id,
    })
      .unwrap()
      .then(() => {
        reset();
      })
      .catch(handleApiError);
  };

  const handleRemoveJob = (job: JobType | Nullish) => {
    if (!job?.id) {
      return;
    }

    openDeleteDialog({
      text: i18n.t('entity.orderRequest.actions.delete', {name: job?.name}),
      'data-testid': suffixTestId('removeJob', props),
      onConfirm: () =>
        deleteServiceOrderJob({
          serviceCaseId: props.serviceCaseId,
          serviceOrderId: props.orderId,
          serviceJobId: job.id ?? '',
        })
          .unwrap()
          .catch(handleApiError),
    });
  };

  const handleMoveJob = (job: JobType | Nullish) => {
    if (!job?.id) {
      return;
    }

    openDialog(
      <MoveRequest
        serviceCaseId={props.serviceCaseId}
        serviceOrderId={props.orderId}
        serviceJobId={job.id}
        onClose={() => closeDialog('moveJobDialog')}
        data-testid={suffixTestId('moveJobDialog', props)}
      />,
      {
        id: 'moveJobDialog',
        title: `${i18n.t('entity.orderRequest.actions.moveRequest')} (${job?.name})`,
      }
    );
  };

  const handlePostponeJob = (job: JobType | Nullish) => {
    if (!job?.id) {
      return;
    }

    openDialog(
      <PostponeDialog
        dialogId="postpone-dialog"
        jobsIds={job.id}
        postponeUntil={null}
        reason={null}
        serviceCaseId={props.serviceCaseId}
        serviceOrderId={props.orderId}
        switchJobToPostponed
        data-testid={suffixTestId('postponeDialog', props)}
      />,
      {
        id: 'postpone-dialog',
        title: i18n.t('entity.serviceCase.labels.postpone'),
        size: 'small',
        withAdditionalFooter: true,
      }
    );
  };

  const onCustomPriceClose = () => {
    closeDialog('customPriceDialog');
    reloadJob();
  };

  const handleCustomPrice = (job: JobType | Nullish) => {
    if (!job) {
      return;
    }

    openDialog(
      <CustomPrice
        serviceCaseId={props.serviceCaseId}
        serviceOrderId={props.orderId}
        job={job}
        onClose={onCustomPriceClose}
      />,
      {
        id: 'customPriceDialog',
        title: i18n.t('entity.orderRequest.actions.customPrice'),
      }
    );
  };

  useEffect(
    function openLastAddedJobDetail() {
      if (isFetching || isNilOrEmpty(data)) {
        return;
      }

      const currentIds = new Set(
        (data ?? []).map((job) => job?.id).filter((id): id is string => Boolean(id))
      );

      if (prevJobIdsRef.current === null) {
        prevJobIdsRef.current = currentIds;
        return;
      }

      const knownIds = prevJobIdsRef.current;
      const newIds = [...currentIds].filter((id) => !knownIds.has(id));
      if (newIds.length > 0) {
        setOpendJobId(newIds[newIds.length - 1]);
      }

      prevJobIdsRef.current = currentIds;
    },
    [data, isFetching, setOpendJobId]
  );

  return (
    <>
      <VStack spacing={4}>
        <DataStatus
          isLoading={isFetching}
          isError={isError}
          isEmpty={isNilOrEmpty(data)}
          minHeight={30}
          data-testid={suffixTestId('orders', props)}
          emptyMessage={i18n.t('entity.order.labels.emptyRequests')}
          emptySubheadline={i18n.t('entity.order.labels.emptyRequestsDescription')}
        >
          {data?.map((job, index) => (
            <Job
              key={`${job?.id ?? index}${jobRefreshKey}`}
              isReadOnly={props.isReadOnly}
              jobData={job}
              serviceCaseId={props.serviceCaseId}
              orderId={props.orderId}
              datagridKey={props.datagridKey}
              actions={[
                {
                  key: 'job-customPrice',
                  label: i18n.t('entity.orderRequest.actions.customPrice'),
                  onClick: () => handleCustomPrice(job),
                },
                {
                  key: 'job-postpone',
                  label: i18n.t('entity.orderRequest.actions.postpone'),
                  onClick: () => handlePostponeJob(job),
                },
                {
                  key: 'job-move',
                  label: i18n.t('entity.orderRequest.actions.moveRequest'),
                  onClick: () => handleMoveJob(job),
                },
                {
                  key: 'job-delete',
                  label: i18n.t('entity.orderRequest.actions.deleteRequest'),
                  onClick: () => handleRemoveJob(job),
                },
              ]}
              onSubmit={handleUpdateJob}
              isReportButtonDisabled={props.isReportButtonDisabled}
              data-testid={suffixTestId(`job-[${index}]`, props)}
            />
          ))}
        </DataStatus>
        <Hide when={props.isReadOnly}>
          <JobInput onSubmit={onSubmit} data-testid={suffixTestId('job', props)} />
        </Hide>
      </VStack>
    </>
  );
}
